"use client";

import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import React, { useContext, useEffect, useRef, useState } from "react";

import Accordion from "@/components/Accordion/Accordion";
import AccordionItem from "@/components/Accordion/AccordionItem";
import DeviceStore from "@/components/DeviceStore/DeviceStore";
import NewsletterForm from "@/components/NewsletterForm/NewsletterForm";
import FooterSocial from "@/components/Social/FooterSocial";
import AnchorLink from "@/components/ui/AnchorLink/AnchorLink";
// import Image from "next/image";
import Image from "@/components/ui/Image/Image";
import { TemplateContext } from "@/context/TemplateContext";
import { footerLinkTracking, policyTracking } from "@/dataLayer/dataLayer";
import useBreakPoint from "@/hooks/useBreakpoint";
import useClientInfo from "@/hooks/useClientInfo";
import useMediaQuery from "@/hooks/useMediaQuery";
import { CustomLink } from "@/types";
import { transparentPixel } from "@/utils/string";

import "./footer.css";
import { FooterProps } from "./types";

interface StoreLink extends CustomLink {
  currentDevice: boolean;
}
export default function Footer({
  damacLogo,
  damacLivingLogo,
  pageClusters,
  legalPages,
  stores: deviceStores,
  socialChannels,
  copyright,
  userAgent,
  newsLetterFormHyperLink,
}: FooterProps) {
  const t = useTranslations();
  const locale = useLocale();
  const { isMd } = useBreakPoint("md");
  const isMobile = useMediaQuery("(max-width: 668px)");
  const client = useClientInfo({ userAgent });
  const [isExpanded, setIsExpanded] = useState<boolean | undefined>(isMd);
  const [stores, setStores] = useState<StoreLink[]>([]);
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  useEffect(() => {
    if (deviceStores && deviceStores?.length > 0) {
      const os = client?.getOS()?.name?.toLowerCase();
      const stores = (deviceStores as StoreLink[]).map((item) => {
        item.currentDevice = false;
        if (!!os) {
          // we only need to test if it an iphone
          if (new RegExp(/apple/i).test(item.label)) {
            if (["mac os", "ios"].includes(os)) {
              item.currentDevice = true;
            }
          } else if (["android"].includes(os)) {
            item.currentDevice = true;
          }
        }
        return item;
      });
      setStores(stores);
    }
  }, [deviceStores, client, userAgent, isMd]);

  useEffect(() => {
    setIsExpanded(isMd);
  }, [isMd]);

  return (
    <footer className="footer w-full z-20">
      <div className="px-m md:container">
        <div className="footer__content">
          {/* LOGO */}
          <div className="footer-logo">
            <div className="flex items-center justify-center h-full md:justify-normal">
              <AnchorLink href={`/${locale}/`} aria-label={t("button.label.homepage")}>
                <Image
                  className="m-auto max-w-[170px] md:m-0 "
                  src={damacLogo?.link?.image?.url || transparentPixel}
                  width={170}
                  height={90}
                  alt={damacLogo?.link?.image?.description || damacLogo?.link?.image?.name || ""}
                  fit="pad"
                />
              </AnchorLink>
            </div>
          </div>
          {/* SOCIAL */}
          <div className="footer-social-links">
            <p className="text-s mt-m mb-s md:hidden">Follow us</p>
            <div className="flex flex-row items-center justify-center md:flex-row md:justify-start h-l md:h-full gap-m">
              {socialChannels?.map((social, i) => (
                <FooterSocial key={`footer-social-${social.id}`} label={social.label} src={`${social.link?.icon}`} url={social.link.url} />
              ))}
            </div>
          </div>
          {/* NEWSLETTER */}
          {isDesktop && (
            <div className="footer-newsletter">
              <NewsletterForm newsLetterFormHyperLink={newsLetterFormHyperLink} />
            </div>
          )}
          {/* LINKS ACCORDION*/}
          <div className={`footer-links ${locale?.split("-")?.[0] === "ar" ? "md:mr-xl" : ""}`}>
            <div className="w-full only-on-mobile md:flex md:gap-xl md:py-l">
              <div className={`flex flex-row md:flex-row ${isMobile ? "flex-wrap" : ""} w-full justify-between`}>
                {pageClusters?.map((item, index) => (
                  <Accordion key={`footer-group${index}`} classes={`text-s w-full md:w-1/4 md:min-w-[200px] invert md:ml-xxl`}>
                    <AccordionItem
                      tag="div"
                      title={item.title}
                      innerTag="h3"
                      accordionId={`accordion-${index}`}
                      expanded={isExpanded}
                      useButton={!isExpanded}
                    >
                      <ul className="px-0 pb-0 mb-xs md:pb-m md:pt-xs">
                        {item.pages?.map((page) => (
                          <li
                            className="py-m font-secondary font-secondary-light text-m md:text-s md:py-xxs"
                            key={`footer-pages-${page.id}`}
                            onClick={() => footerLinkTracking(item.title, page.label)}
                          >
                            <AnchorLink className="hover:underline underline-offset-4" href={getLink(page, locale).toString()}>
                              {page.label}
                            </AnchorLink>
                          </li>
                        ))}
                      </ul>
                    </AccordionItem>
                  </Accordion>
                ))}
              </div>
            </div>
          </div>
          {/* DEVICE STORE */}
          {/* <div className="footer-device-store">
            <div className="flex flex-col items-center w-full pt-m md:pt-0">
              {damacLivingLogo && (
                <AnchorLink href={damacLivingLogo?.link?.url || ""}>
                  <Image
                    className="self-center"
                    src={damacLivingLogo?.link?.image?.url || transparentPixel}
                    width={170}
                    height={80}
                    fit="pad"
                    alt={damacLivingLogo?.link?.image?.description || damacLivingLogo?.link?.image?.name || ""}
                  />
                </AnchorLink>
              )}
              <div className="flex flex-row justify-center flex-wrap max-w-[270px] gap-m mt-m mb-m">
                {stores?.map((store, index) => {
                  return (
                    <div key={`store-${index}`} className={`${store.currentDevice ? "" : "hidden md:block"}`}>
                      <DeviceStore img={`${store?.link?.image?.url}`} url={store?.link?.url} alt={store?.label} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div> */}
          {/* BOTTOM LINKS */}
          <div className="footer-bottom">
            <div className="footer-bottom-links">
              {legalPages && (
                <ul className="footer-bottom-link-items">
                  {legalPages.map((item) => (
                    <li key={`footer-bottom-link-${item.id}`}>
                      <AnchorLink href={getLink(item, locale).toString()} onClick={() => policyTracking(item.label)}>
                        {item.label}
                      </AnchorLink>
                    </li>
                  ))}
                </ul>
              )}
              <p>
                {copyright}
                {` `}
                <small>({`v${process.env?.VERSION ?? ""}`})</small>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
